class CustomSelect {
  /**
   * @param {HTMLSelectElement} selectEl - the native select element
   * @param {Object} options - optional configuration
   *   {string} placeholderText - text used when value is empty
   *   {function} validator - optional custom validator (value) => { valid: boolean, message?: string }
   */
  constructor(selectEl, options = {}) {
    if (!(selectEl instanceof HTMLSelectElement)) {
      throw new Error("CustomSelect requires a native <select> element");
    }
    this.select = selectEl;
    this.options = Object.assign({
      placeholderText: null,
      labelText: null,
      fullWidth: true,
      validator: null,
      timeMode: false
    }, options);
    this._build();
    this._attachEvents();
    this._syncFromSelect();
  }
  _build() {
    this.container = document.createElement("div");
    this.container.className = "custom-select w-full relative block";
    this.container.tabIndex = -1;
    this.container.setAttribute("aria-hidden", "false");
    this.select.classList.add(
      "custom-select__native",
      "absolute",
      "opacity-0",
      "pointer-events-none",
      "h-0",
      "w-0"
    );
    if (this.options.labelText) {
      this.labelEl = document.createElement("label");
      this.labelEl.className = "custom-select__field-label redesign-body-5 block mb-12";
      this.labelEl.innerHTML = this.options.labelText;
      const selectId = this.select.id || `select-${Math.random().toString(36).substr(2, 5)}`;
      this.select.id = selectId;
      this.labelEl.setAttribute("for", selectId);
      this.container.appendChild(this.labelEl);
    }
    this.trigger = document.createElement("button");
    this.trigger.type = "button";
    this.trigger.className = "custom-select__trigger redesign-body-8 w-full h-46 ps-16 py-10 border border-solid border-charcoal-10 text-charcoal-100 cursor-pointer focus:outline-0 focus-visible:outline-0 flex items-center justify-between";
    this.trigger.setAttribute("aria-haspopup", "listbox");
    this.trigger.setAttribute("aria-expanded", "false");
    this.labelSpan = document.createElement("span");
    this.labelSpan.className = "custom-select__label";
    this.trigger.appendChild(this.labelSpan);
    const care = document.createElement("span");
    care.className = "custom-select__caret h-44 w-44 border-l-1 border-solid border-charcoal-10 bg-no-repeat bg-center";
    this.trigger.appendChild(care);
    this.panel = document.createElement("div");
    this.panel.className = "custom-select__panel redesign-body-8 bg-ivory absolute z-[1000] left-0 right-0 mt-0 overflow-auto";
    this.panel.setAttribute("role", "listbox");
    this.panel.setAttribute("tabindex", "-1");
    this.errorEl = document.createElement("div");
    this.errorEl.className = "custom-select__error text-red-100 redesign-body-9 mt-6";
    this.errorEl.setAttribute("aria-live", "polite");
    this.select.parentNode.insertBefore(this.container, this.select);
    this.container.appendChild(this.select);
    this.container.appendChild(this.trigger);
    this.container.appendChild(this.panel);
    this.container.appendChild(this.errorEl);
    this._renderOptions();
  }
  _renderOptions() {
    this.panel.innerHTML = "";
    this.items = [];
    let opts = Array.from(this.select.options);
    console.debug("Options", opts);
    if (this.options.timeMode) {
      const times = this._generateTimeOptions();
      this.select.innerHTML = "";
      times.forEach((t) => {
        const o = document.createElement("option");
        o.value = t.value;
        o.textContent = t.label;
        if (!t.value) {
          o.disabled = true;
          o.selected = true;
        }
        this.select.appendChild(o);
      });
      opts = Array.from(this.select.options);
    }
    Array.from(this.select.options).forEach((opt, idx) => {
      const item = document.createElement("div");
      item.className = "custom-select__option border-l-1 border-r-1 border-b-1 leading-23 border-solid border-charcoal-10 px-16 py-10 cursor-pointer";
      item.setAttribute("role", "option");
      item.setAttribute("data-value", opt.value);
      item.setAttribute("data-index", idx);
      item.tabIndex = -1;
      item.textContent = opt.textContent;
      if (opt.disabled) {
        item.setAttribute("aria-disabled", "true");
        item.style.opacity = "0.6";
        item.style.pointerEvents = "none";
      }
      if (opt.hidden) item.style.display = "none";
      this.panel.appendChild(item);
      this.items.push(item);
    });
  }
  _attachEvents() {
    this._onTriggerClick = (e) => {
      e.preventDefault();
      this.toggle();
    };
    this.trigger.addEventListener("click", this._onTriggerClick);
    this._onOptionClick = (e) => {
      const item = e.target.closest(".custom-select__option");
      if (!item) return;
      this._selectItem(item);
      this.close();
      this.trigger.focus();
    };
    this.panel.addEventListener("click", this._onOptionClick);
    this._onTriggerKeyDown = (e) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (!this.isOpen) this.open();
          this._focusNext(0);
          break;
        case "ArrowUp":
          e.preventDefault();
          if (!this.isOpen) this.open();
          this._focusPrev(this.items.length - 1);
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          this.toggle();
          break;
        case "Escape":
          this.close();
          break;
      }
    };
    this.trigger.addEventListener("keydown", this._onTriggerKeyDown);
    this._onPanelKeyDown = (e) => {
      const currentIndex = this._focusedIndex ?? -1;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this._focusNext(currentIndex + 1);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        this._focusPrev(currentIndex - 1);
      } else if (e.key === "Home") {
        e.preventDefault();
        this._focusIndex(0);
      } else if (e.key === "End") {
        e.preventDefault();
        this._focusIndex(this.items.length - 1);
      } else if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        const item = this.items[this._focusedIndex];
        if (item) {
          this._selectItem(item);
          this.close();
          this.trigger.focus();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.close();
        this.trigger.focus();
      } else if (e.key === "Tab") {
        this.close();
      }
    };
    this.panel.addEventListener("keydown", this._onPanelKeyDown);
    this._onDocClick = (e) => {
      if (!this.container.contains(e.target)) this.close();
    };
    document.addEventListener("click", this._onDocClick, true);
    this._onNativeChange = () => this._syncFromSelect();
    this.select.addEventListener("change", this._onNativeChange);
    this._onFormSubmit = (e) => {
      var _a, _b;
      if (!this.validate()) {
        e.preventDefault();
        (_b = (_a = this.select).reportValidity) == null ? void 0 : _b.call(_a);
        this.trigger.focus();
      }
    };
    this.form = this.select.closest("form");
    if (this.form) {
      this.form.addEventListener("submit", this._onFormSubmit);
    }
  }
  _generateTimeOptions() {
    const hours = [];
    hours.push({
      value: "",
      label: this.options.placeholderText || "Select Time"
    });
    for (let i = 0; i < 24; i++) {
      const hour12 = i % 12 === 0 ? 12 : i % 12;
      const ampm = i < 12 ? "AM" : "PM";
      hours.push({
        value: `${hour12}${ampm}`,
        label: `${hour12}${ampm}`
      });
    }
    return hours;
  }
  _syncFromSelect() {
    const value = this.select.value;
    const selectedOption = this.select.options[this.select.selectedIndex];
    const labelText = selectedOption ? selectedOption.textContent : "";
    if (!value) {
      const placeholder = this.select.querySelector('option[value=""]');
      if (placeholder && placeholder.textContent.trim()) {
        this.labelSpan.textContent = placeholder.textContent.trim();
      } else if (this.options.placeholderText) {
        this.labelSpan.textContent = this.options.placeholderText;
      } else {
        this.labelSpan.textContent = "";
      }
      this.labelSpan.classList.add("custom-select__placeholder", "redesign-body-8");
    } else {
      this.labelSpan.textContent = labelText;
      this.labelSpan.classList.remove("custom-select__placeholder");
    }
    this.items.forEach((item) => {
      const val = item.getAttribute("data-value");
      item.setAttribute("aria-selected", String(val === value));
    });
    if (!this._lastValidationValid && this._lastValidationValid !== void 0) {
      this.container.classList.add("custom-select--invalid");
    } else {
      this.container.classList.remove("custom-select--invalid");
    }
  }
  _selectItem(item) {
    const value = item.getAttribute("data-value");
    const idx = parseInt(item.getAttribute("data-index"));
    if (this.select.selectedIndex === idx && this.select.value === value) {
      return;
    }
    this.select.selectedIndex = idx;
    const evt = new Event("change", { bubbles: true });
    this.select.dispatchEvent(evt);
    this._syncFromSelect();
    this.clearError();
  }
  open() {
    if (this.isOpen) return;
    this.isOpen = true;
    this.container.classList.add("custom-select--open");
    this.trigger.setAttribute("aria-expanded", "true");
    this.panel.style.minWidth = `${this.trigger.offsetWidth}px`;
    const selIndex = this.select.selectedIndex >= 0 ? this.select.selectedIndex : 0;
    this._focusIndex(selIndex);
    this.panel.focus();
  }
  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.container.classList.remove("custom-select--open");
    this.trigger.setAttribute("aria-expanded", "false");
    this._focusedIndex = void 0;
  }
  toggle() {
    if (this.isOpen) this.close();
    else this.open();
  }
  _focusIndex(i) {
    i = Math.max(0, Math.min(this.items.length - 1, i));
    this._focusedIndex = i;
    const item = this.items[i];
    if (!item) return;
    item.focus();
    item.scrollIntoView({ block: "nearest" });
    this.items.forEach((it, idx) => {
      it.classList.toggle("focused", idx === i);
    });
  }
  _focusNext(start) {
    for (let i = start; i < this.items.length; i++) {
      const it = this.items[i];
      if (it.getAttribute("aria-disabled") !== "true" && it.style.display !== "none") {
        this._focusIndex(i);
        break;
      }
    }
  }
  _focusPrev(start) {
    for (let i = start; i >= 0; i--) {
      const it = this.items[i];
      if (it.getAttribute("aria-disabled") !== "true" && it.style.display !== "none") {
        this._focusIndex(i);
        break;
      }
    }
  }
  validate() {
    if (typeof this.options.validator === "function") {
      const result = this.options.validator(this.select.value);
      if (!result || result.valid === false) {
        this.setError(result && result.message ? result.message : "Invalid selection");
        this._lastValidationValid = false;
        return false;
      } else {
        this.clearError();
        this._lastValidationValid = true;
        return true;
      }
    }
    if (this.select.required) {
      const ok = this.select.value !== "" && this.select.value !== null;
      this._lastValidationValid = ok;
      if (!ok) {
        const msg = this.select.getAttribute("data-error-message") || this.select.getAttribute("title") || "Please select an option.";
        this.setError(msg);
        return false;
      } else {
        this.clearError();
        return true;
      }
    }
    this.clearError();
    this._lastValidationValid = true;
    return true;
  }
  setError(message) {
    this.errorEl.textContent = message;
    this.container.classList.add("custom-select--invalid");
    this.select.setCustomValidity(message || "Invalid");
    this.errorEl.setAttribute("role", "alert");
  }
  clearError() {
    this.errorEl.textContent = "";
    this.container.classList.remove("custom-select--invalid");
    this.select.setCustomValidity("");
    this.errorEl.removeAttribute("role");
  }
  destroy() {
    this.trigger.removeEventListener("click", this._onTriggerClick);
    this.panel.removeEventListener("click", this._onOptionClick);
    this.trigger.removeEventListener("keydown", this._onTriggerKeyDown);
    this.panel.removeEventListener("keydown", this._onPanelKeyDown);
    document.removeEventListener("click", this._onDocClick, true);
    this.select.removeEventListener("change", this._onNativeChange);
    if (this.form) this.form.removeEventListener("submit", this._onFormSubmit);
    this.container.parentNode.insertBefore(this.select, this.container);
    this.select.classList.remove("custom-select__native");
    this.container.remove();
  }
}
(() => {
  const giftFormEl = document.querySelector("#giftcardForm");
  if (!giftFormEl) return false;
  const selectVariantId = document.querySelector('[name="id"]');
  if (selectVariantId) {
    const firstSelected = selectVariantId.querySelector("option[selected]");
    if (firstSelected) firstSelected.removeAttribute("selected");
    if (!selectVariantId.querySelector('option[value=""]')) {
      const placeholder = document.createElement("option");
      placeholder.value = "";
      placeholder.textContent = "Select Amount";
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.hidden = true;
      selectVariantId.insertBefore(placeholder, selectVariantId.firstChild);
    }
    selectVariantId.value = "";
    const variantSelect = new CustomSelect(selectVariantId, {
      labelText: 'Amount: <span class="redesign-body-7">Choose amount</span>',
      placeholderText: "Select Amount",
      validator: (value) => {
        if (value === "") return { valid: false, message: "Choose amount please" };
        return { valid: true };
      }
    });
    const updateAmountLabel = () => {
      var _a;
      const selected = selectVariantId.options[selectVariantId.selectedIndex];
      if (!variantSelect.labelEl) return;
      const labelText = ((_a = selected == null ? void 0 : selected.innerHTML) == null ? void 0 : _a.trim()) || "Choose amount";
      variantSelect.labelEl.querySelector("span").textContent = labelText;
    };
    const updateVariantURL = () => {
      const selected = selectVariantId.value;
      if (!selected) return;
      const url = new URL(window.location.href);
      url.searchParams.set("variant", selected);
      window.history.replaceState({}, "", url);
    };
    const handleVariantChange = () => {
      updateAmountLabel();
      updateVariantURL();
      selectVariantId.required = true;
    };
    selectVariantId.addEventListener("change", handleVariantChange);
    const params = new URLSearchParams(window.location.search);
    const variantId = params.get("variant");
    if (variantId) {
      const match = Array.from(selectVariantId.options).find((opt) => opt.value === variantId);
      if (match) {
        selectVariantId.value = variantId;
        selectVariantId.dispatchEvent(new Event("change", { bubbles: true }));
      }
    }
  }
  const deliveryTime = document.querySelector("#delivery-time");
  if (deliveryTime) {
    deliveryTime.required = true;
    new CustomSelect(deliveryTime, {
      placeholderText: "Select Time",
      timeMode: true,
      validator: (value) => {
        if (value === "") return { valid: false, message: "Choose time" };
        return { valid: true };
      }
    });
  }
  const deliveryTimezone = document.querySelector("#delivery-timezone");
  if (deliveryTimezone) {
    new CustomSelect(deliveryTimezone, {
      placeholderText: "Select Timezone",
      validator: (value) => {
        if (value === "") return { valid: false, message: "Choose timezone" };
        return { valid: true };
      }
    });
  }
  const dateInput = document.getElementById("giftcard_delivery_date");
  const timeSelect = document.getElementById("delivery-time");
  const tzSelect = document.getElementById("delivery-timezone");
  const isoInput = document.getElementById("giftcard_delivery_date_rfc_3339");
  const recipientInput = document.getElementById("giftcard_recipient_name");
  const recipientHidden = document.getElementById("giftcard_recipient_name_internal");
  const recipientMessageInput = document.getElementById("giftcard_note");
  const recipientMessageHidden = document.getElementById("giftcard_gift_message_internal");
  const recipientEmailInput = document.getElementById("giftcard_recipient_email");
  const recipientEmailHidden = document.getElementById("giftcard_recipient_email_internal");
  function syncHiddenField(visibleEl, hiddenEl) {
    if (!visibleEl || !hiddenEl) return;
    const sync = () => {
      hiddenEl.value = visibleEl.value;
      hiddenEl.setCustomValidity("");
    };
    visibleEl.addEventListener("input", sync);
    visibleEl.addEventListener("change", sync);
    sync();
  }
  syncHiddenField(recipientInput, recipientHidden);
  syncHiddenField(recipientMessageInput, recipientMessageHidden);
  syncHiddenField(recipientEmailInput, recipientEmailHidden);
  const TZ_OFFSETS = {
    "Eastern Time": "-05:00",
    "Central Time": "-06:00",
    "Mountain Time": "-07:00",
    "Pacific Time": "-08:00",
    Arizona: "-07:00",
    Alaska: "-09:00",
    Hawaii: "-10:00"
  };
  function parseHour24(timeStr) {
    const match = /^(\d{1,2})(AM|PM)$/i.exec(timeStr || "");
    if (!match) return null;
    let hour = parseInt(match[1], 10);
    const ampm = match[2].toUpperCase();
    if (ampm === "AM") {
      if (hour === 12) hour = 0;
    } else if (hour !== 12) {
      hour += 12;
    }
    return hour.toString().padStart(2, "0");
  }
  function updateIsoField() {
    if (!dateInput || !timeSelect || !tzSelect || !isoInput) return;
    const dateVal = dateInput.value;
    const timeVal = timeSelect.value;
    const tzLabel = tzSelect.value;
    if (!dateVal || !timeVal || !tzLabel) {
      isoInput.value = "";
      isoInput.setCustomValidity("");
      return;
    }
    const hour24 = parseHour24(timeVal);
    const offset = TZ_OFFSETS[tzLabel];
    if (!hour24 || !offset) {
      isoInput.value = "";
      isoInput.setCustomValidity("Invalid date, time, or timezone selection");
      return;
    }
    const localWithOffset = `${dateVal}T${hour24}:00:00${offset}`;
    const isoString = new Date(localWithOffset).toISOString();
    isoInput.value = isoString;
    isoInput.setCustomValidity("");
  }
  if (dateInput && timeSelect && tzSelect && isoInput) {
    dateInput.addEventListener("change", updateIsoField);
    timeSelect.addEventListener("change", updateIsoField);
    tzSelect.addEventListener("change", updateIsoField);
    updateIsoField();
  }
  const form = document.querySelector("form#addToBagForm");
  if (form) {
    const submitBtn = form.querySelector('[type="submit"] span');
    if (submitBtn) {
      const defaultText = submitBtn.dataset.defaultText || submitBtn.textContent.trim();
      const validText = "Add to Cart";
      submitBtn.dataset.defaultText = defaultText;
      const updateButtonText = () => {
        const requiredEls = Array.from(form.querySelectorAll("[required]"));
        const allRequiredValid = requiredEls.every((el) => el.checkValidity());
        submitBtn.textContent = allRequiredValid ? validText : submitBtn.dataset.defaultText;
      };
      form.addEventListener("input", updateButtonText, true);
      form.addEventListener("change", updateButtonText, true);
      updateButtonText();
    }
  }
})();
